import i18n from '@/i18n'
import { getHostMode, isDedicatedHost, counterpartOrigin } from '@/config/host.config'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { useUIStore } from '@/stores/ui.store'
import { ROLE_PERMISSIONS, type PermissionKey } from '@/config/permissions.config'

// Auth Pages
const LoginView = () => import('@/views/auth/LoginView.vue')
const ForgotPasswordView = () => import('@/views/auth/ForgotPasswordView.vue')
const ResetPasswordView = () => import('@/views/auth/ResetPasswordView.vue')
const ForceChangePasswordView = () => import('@/views/auth/ForceChangePasswordView.vue')
// Dashboard Pages
const HomeView = () => import('@/views/dashboard/HomeView.vue')
const EmployeeListView = () => import('@/views/dashboard/EmployeeListView.vue')
const EmployeeFormView = () => import('@/views/dashboard/EmployeeFormView.vue')
const EmployeeDetailView = () => import('@/views/dashboard/EmployeeDetailView.vue')
const ProjectListView = () => import('@/views/dashboard/ProjectListView.vue')
const RequestsView = () => import('@/views/dashboard/RequestsView.vue')
const PayrollView = () => import('@/views/dashboard/PayrollView.vue')
const DocumentsView = () => import('@/views/dashboard/DocumentsView.vue')
const ReportsView = () => import('@/views/dashboard/ReportsView.vue')
const SettingsView = () => import('@/views/dashboard/SettingsView.vue')
const OrgHierarchyView = () => import('@/views/dashboard/OrgHierarchyView.vue')
const AuditLogsView = () => import('@/views/dashboard/AuditLogsView.vue')
const InventoryView = () => import('@/views/dashboard/InventoryView.vue')
const ProcurementRequestsView = () => import('@/views/dashboard/ProcurementRequestsView.vue')
const MyAssetsView = () => import('@/views/dashboard/MyAssetsView.vue')
const DisciplineView = () => import('@/views/dashboard/DisciplineView.vue')
const NotificationSendView = () => import('@/views/dashboard/NotificationSendView.vue')

// Portal Pages
const PortalHomeView = () => import('@/views/portal/PortalHomeView.vue')
const PortalRequestsView = () => import('@/views/portal/PortalRequestsView.vue')
const PortalProcurementRequestsView = () => import('@/views/portal/PortalProcurementRequestsView.vue')
const PortalAssetsView = () => import('@/views/portal/PortalAssetsView.vue')
const PortalLeaveBalanceView = () => import('@/views/portal/PortalLeaveBalanceView.vue')
const PortalDocumentsView = () => import('@/views/portal/PortalDocumentsView.vue')
const PortalPayrollsView = () => import('@/views/portal/PortalPayrollsView.vue')

// Other Pages
const NotFoundView = () => import('@/views/NotFoundView.vue')

export const routes: RouteRecordRaw[] = [
  // Root redirect
  {
    // employee.hrtakip.com kökünden portala, diğerlerinde panele
    path: '/',
    redirect: () => (getHostMode() === 'employee' ? '/portal' : '/dashboard'),
  },

  // ─── Auth Routes (guest only) ───────────────────────────────
  {
    path: '/login',
    component: LoginView,
    meta: { guest: true, layout: 'auth', titleKey: 'routes.login' }
  },
  {
    path: '/portal-login',
    redirect: '/login',
  },
  {
    path: '/forgot-password',
    component: ForgotPasswordView,
    meta: { guest: true, layout: 'auth', titleKey: 'routes.forgotPassword' }
  },
  {
    path: '/reset-password/:token',
    component: ResetPasswordView,
    meta: { guest: true, layout: 'auth', titleKey: 'routes.resetPassword' }
  },
  {
    path: '/change-password',
    component: ForceChangePasswordView,
    meta: { requiresAuth: true, layout: 'auth', titleKey: 'routes.setPassword', forceChangePassword: true }
  },

  // ─── Dashboard Routes (auth required) ──────────────────────
  {
    path: '/dashboard',
    meta: { requiresAuth: true, layout: 'dashboard' },
    children: [
      {
        path: '',
        component: HomeView,
        meta: { titleKey: 'routes.dashboard' }
      },
      {
        path: 'employees',
        component: EmployeeListView,
        meta: { titleKey: 'routes.employees', requiredPermission: 'view_employees' }
      },
      {
        path: 'employees/new',
        name: 'employee-create',
        component: EmployeeFormView,
        meta: { titleKey: 'routes.employeeNew', requiredPermission: 'create_employee' }
      },
      {
        path: 'employees/add',
        redirect: { name: 'employee-create' },
      },
      {
        path: 'employees/:id',
        name: 'employee-detail',
        component: EmployeeDetailView,
        meta: { titleKey: 'routes.employeeDetail', requiredPermission: 'view_employees' }
      },
      {
        path: 'employees/:id/edit',
        name: 'employee-edit',
        component: EmployeeFormView,
        meta: { titleKey: 'routes.employeeEdit', requiredPermission: 'edit_employee' }
      },
      {
        path: 'projects',
        component: ProjectListView,
        meta: { titleKey: 'routes.projects', requiredPermission: 'view_projects' }
      },
      {
        path: 'companies',
        redirect: { path: '/dashboard/projects' }
      },
      {
        path: 'requests',
        component: RequestsView,
        meta: { titleKey: 'routes.requests', requiredPermission: 'view_requests' }
      },
      {
        path: 'inventory',
        component: InventoryView,
        meta: { titleKey: 'routes.inventory', requiredPermission: 'view_inventory' }
      },
      {
        path: 'procurement-requests',
        component: ProcurementRequestsView,
        meta: { titleKey: 'routes.procurement', requiredPermission: 'view_procurement_requests' }
      },
      {
        path: 'my-assets',
        component: MyAssetsView,
        meta: { titleKey: 'routes.myAssets' }
      },
      {
        path: 'discipline',
        component: DisciplineView,
        meta: { titleKey: 'routes.discipline', requiredPermission: 'manage_discipline' }
      },
      {
        path: 'send-notification',
        component: NotificationSendView,
        meta: { titleKey: 'routes.sendNotification', requiredPermission: 'send_notification' }
      },
      {
        path: 'payroll',
        component: PayrollView,
        meta: { titleKey: 'routes.payroll', requiredPermission: 'view_payroll' }
      },
      {
        path: 'documents',
        component: DocumentsView,
        meta: { titleKey: 'routes.documents', requiredPermission: 'view_documents' }
      },
      {
        path: 'reports',
        component: ReportsView,
        meta: { titleKey: 'routes.reports', requiredPermission: 'view_reports' }
      },
      {
        path: 'settings',
        component: SettingsView,
        meta: { titleKey: 'routes.settings', requiredPermission: 'view_settings' }
      },
      {
        path: 'org-hierarchy',
        component: OrgHierarchyView,
        meta: { titleKey: 'routes.org', requiredPermission: 'view_org_hierarchy' }
      },
      {
        path: 'logs',
        component: AuditLogsView,
        meta: { titleKey: 'routes.logs', requiredPermission: 'view_audit_logs' }
      },
      {
        path: 'audit-logs',
        redirect: '/dashboard/logs'
      }
    ]
  },

  // ─── Portal Routes (auth required, employee only) ──────────
  {
    path: '/portal',
    meta: { requiresAuth: true, layout: 'portal' },
    children: [
      {
        path: '',
        component: PortalHomeView,
        meta: { titleKey: 'routes.portalHome' }
      },
      {
        path: 'requests',
        component: PortalRequestsView,
        meta: { titleKey: 'routes.portalRequests' }
      },
      {
        path: 'procurement-requests',
        component: PortalProcurementRequestsView,
        meta: { titleKey: 'routes.portalProcurement' }
      },
      {
        path: 'assets',
        component: PortalAssetsView,
        meta: { titleKey: 'routes.portalAssets' }
      },
      {
        path: 'leave-balance',
        component: PortalLeaveBalanceView,
        meta: { titleKey: 'routes.portalLeave' }
      },
      {
        path: 'documents',
        component: PortalDocumentsView,
        meta: { titleKey: 'routes.portalDocuments' }
      },
      {
        path: 'payrolls',
        component: PortalPayrollsView,
        meta: { titleKey: 'routes.portalPayrolls' }
      }
    ]
  },

  // ─── 404 ───────────────────────────────────────────────────
  {
    path: '/:pathMatch(.*)*',
    component: NotFoundView,
    meta: { layout: 'empty', titleKey: 'routes.notFound' }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

// ─── Single unified navigation guard ──────────────────────────
let authInitialized = false

// ─── Global yükleme çubuğu: navigasyon başında göster ─────────
let navLoadingActive = false

router.beforeEach((to, from) => {
  // Aynı sayfada (yalnızca query/hash değişikliği) çubuğu gösterme.
  // navLoadingActive guard'ı redirect zincirlerinde sayacın çift artmasını önler.
  if (to.path !== from.path && !navLoadingActive) {
    useUIStore().startLoading()
    navLoadingActive = true
  }
  return true
})

function endNavLoading() {
  if (navLoadingActive) {
    useUIStore().stopLoading()
    navLoadingActive = false
  }
}

/**
 * Alan adı bazlı katı ayırım.
 * admin.hrtakip.com yalnızca yönetim panelini, employee.hrtakip.com yalnızca
 * çalışan portalını sunar. Yanlış tarafa gelen istek doğru alan adına gider.
 */
router.beforeEach((to) => {
  if (!isDedicatedHost()) return true

  const mode = getHostMode()
  const isPortalRoute = to.path === '/portal' || to.path.startsWith('/portal/')
  const isDashboardRoute = to.path === '/dashboard' || to.path.startsWith('/dashboard/')

  if (mode === 'admin' && isPortalRoute) {
    if (import.meta.env.PROD) {
      window.location.replace(`${counterpartOrigin('employee')}${to.fullPath}`)
      return false
    }
    return { path: '/dashboard' }
  }

  if (mode === 'employee' && isDashboardRoute) {
    if (import.meta.env.PROD) {
      window.location.replace(`${counterpartOrigin('admin')}${to.fullPath}`)
      return false
    }
    return { path: '/portal' }
  }

  return true
})

router.beforeEach(async (to, _from) => {
  const authStore = useAuthStore()

  // Initialize auth from localStorage once
  if (!authInitialized) {
    authStore.initAuth()
    authInitialized = true
  }

  const isAuthenticated = authStore.isAuthenticated
  const isGuestRoute = to.meta.guest === true

  // Check if this route (or any parent) requires auth
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth === true)

  // 1) Authenticated user trying to visit guest-only pages → home for their app
  if (isGuestRoute && isAuthenticated) {
    return authStore.isPortalUser ? { path: '/portal' } : { path: '/dashboard' }
  }

  // 2) Unauthenticated user trying to visit protected pages → /login (portal için Çalışan sekmesi)
  if (requiresAuth && !isAuthenticated) {
    const restored = await authStore.restoreSessionFromCookie()
    if (restored) {
      return true
    }

    const fromPortal = to.path === '/portal' || to.path.startsWith('/portal/')
    return {
      path: '/login',
      query: fromPortal
        ? { redirect: to.fullPath, tab: 'employee' }
        : { redirect: to.fullPath },
    }
  }

  // 2b) Çalışan (portal) JWT → yönetim paneli rotalarına izin yok
  if (requiresAuth && isAuthenticated && authStore.isPortalUser) {
    const isDashboard = to.path === '/dashboard' || to.path.startsWith('/dashboard/')
    if (isDashboard) {
      return { path: '/portal' }
    }
  }

  // 2c) Kurumsal kullanıcı → çalışan portalı rotalarına yönlendirme (TC ile giriş gerekir)
  if (requiresAuth && isAuthenticated && !authStore.isPortalUser) {
    const isPortalApp = to.path === '/portal' || to.path.startsWith('/portal/')
    if (isPortalApp) {
      return { path: '/dashboard' }
    }
  }

  // 3) Protected route + session: renew expired access JWT or force logout
  if (requiresAuth && isAuthenticated) {
    const session = await authStore.ensureValidAccessToken()
    if (!session.ok) {
      const fromPortal = to.path === '/portal' || to.path.startsWith('/portal/')
      return {
        path: session.loginPath,
        query: fromPortal
          ? { redirect: to.fullPath, tab: 'employee' }
          : { redirect: to.fullPath },
      }
    }
  }

  // 4) Route permission (dashboard child routes with requiredPermission meta)
  if (requiresAuth && isAuthenticated && authStore.user) {
    const chain = to.matched
      .map((r) => r.meta.requiredPermission as PermissionKey | undefined)
      .filter((p): p is PermissionKey => Boolean(p))
    const requiredPerm = chain[chain.length - 1]
    if (requiredPerm) {
      const allowed = ROLE_PERMISSIONS[authStore.user.role]?.includes(requiredPerm)
      if (!allowed) {
        return { path: '/dashboard' }
      }
    }
  }

  // 5) All good → proceed
  return true
})

// ─── Update page title + yükleme çubuğunu kapat ───────────────
router.afterEach((to) => {
  endNavLoading()

  const key = to.meta.titleKey as string | undefined
  const appName = i18n.global.t('routes.appName')
  document.title = key ? `${i18n.global.t(key)} — ${appName}` : appName
})

// Navigasyon hata verirse çubuğu takılı bırakma
router.onError(() => {
  endNavLoading()
})

export default router
